import React, { useEffect, useState } from 'react';
import { Box, Typography, List, ListItem, ListItemText, Chip, Link as MLink } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { apiService } from '../../services/apiService';

interface SimilarItem {
  bioguide_id: string;
  name: string;
  party?: string | null;
  state?: string | null;
  district?: string | number | null;
  similarity: number;
}

const partyChipColor = (p?: string | null): 'default' | 'primary' | 'secondary' => {
  switch ((p || '').toLowerCase()) {
    case 'democratic': return 'primary';
    case 'republican': return 'secondary';
    default: return 'default';
  }
};

export const SimilarMembersList: React.FC<{ bioguideId: string; top?: number }>
  = ({ bioguideId, top = 10 }) => {
  const [items, setItems] = useState<SimilarItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    try {
      setError(null);
      const resp = await apiService.getSimilarMembers(bioguideId, top);
      setItems(resp.data?.items || []);
    } catch (e: any) {
      setError(e?.message || 'Failed to load similar members');
      setItems([]);
    }
  };

  useEffect(() => { load(); }, [bioguideId, top]);

  return (
    <Box>
      <Typography variant="h6" gutterBottom>Similar Members</Typography>
      {error && <Typography variant="body2" color="error" sx={{ mb: 1 }}>{error}</Typography>}
      {items === null ? (
        <Typography variant="body2" color="textSecondary">Loading…</Typography>
      ) : items.length === 0 ? (
        <Typography variant="body2" color="textSecondary">No similar members found.</Typography>
      ) : (
        <List dense>
          {items.map((m) => {
            const displayName = (m.name && m.name.trim()) ? m.name.trim() : m.bioguide_id;
            const loc = `${m.state || ''}${m.district ? `-${m.district}` : ''}`;
            return (
              <ListItem key={m.bioguide_id} divider>
                <ListItemText
                  primary={
                    <MLink component={RouterLink} to={`/members/${m.bioguide_id}`} underline="hover" color="primary">
                      {displayName}
                    </MLink>
                  }
                  secondary={
                    <>
                      {(m.state || m.district) && (
                        <span>{loc}</span>
                      )}
                      <span> • Similarity: {(m.similarity * 100).toFixed(0)}%</span>
                    </>
                  }
                />
                {m.party && (
                  <Chip size="small" label={m.party} color={partyChipColor(m.party)} />
                )}
              </ListItem>
            );
          })}
        </List>
      )}
    </Box>
  );
};

export default SimilarMembersList;
